import { useMemo, useState } from 'react'
import type { FC } from 'react'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  Divider,
  FormControlLabel,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import CallMergeRoundedIcon from '@mui/icons-material/CallMergeRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import type { AxiosError } from 'axios'
import type { StatesListResponse } from '@fireflyhealth/api'

import { useStates } from '~/entities/state'

import { StatusPill } from '~/shared/ui'
import { queueNotification } from '~/shared/notifications/actions'
import {
  useMergePersonInto,
  useVerifiedPersonSearch,
  type VerifiedPerson,
  type VerifiedPersonSearchParams,
} from './PersonMergeService'
import { required } from '~/shared/lib/required'

/** The unknown (placeholder) Person the merge is anchored on — the merge source. */
export interface MergeSource {
  id: number
  firstName: string | null
  lastName: string | null
  dob: string | null
  memberId: string | null
  streetAddress: string | null
  city: string | null
  zipcode: string | null
  state: string | null
}

interface VerifiedMergeComposerProps {
  source: MergeSource
}

type Stage = 'search' | 'confirm'

// The second factor ops picks between: member ID, or the member's home address.
// Exactly one is searched/merged by at a time.
type SecondFactor = 'member_id' | 'address'

// Demographics are always required, on top of whichever second factor is active.
const DEMOGRAPHIC_FIELDS = [
  ['first_name', 'First name'],
  ['last_name', 'Last name'],
  ['dob', 'Date of birth (YYYY-MM-DD)'],
] as const

const MEMBER_ID_FIELDS = [['member_id', 'Member ID']] as const
const ADDRESS_FIELDS = [
  ['street_address', 'Street address'],
  ['city', 'City'],
  ['zipcode', 'ZIP code'],
  ['state', 'State'],
] as const

type SearchFilterKey =
  | (typeof DEMOGRAPHIC_FIELDS)[number][0]
  | (typeof MEMBER_ID_FIELDS)[number][0]
  | (typeof ADDRESS_FIELDS)[number][0]

// A single concrete element type (rather than a union of the two tuple shapes) so mapping over
// `SECOND_FACTOR_FIELDS[secondFactor]` resolves to one callback signature.
const SECOND_FACTOR_FIELDS: Record<
  SecondFactor,
  ReadonlyArray<readonly [SearchFilterKey, string]>
> = {
  member_id: MEMBER_ID_FIELDS,
  address: ADDRESS_FIELDS,
}

// Short labels for the confirm-stage changed-details diff.
const CONFIRM_FIELD_LABEL: Record<SearchFilterKey, string> = {
  first_name: 'First name',
  last_name: 'Last name',
  dob: 'Date of birth',
  member_id: 'Member ID',
  street_address: 'Street address',
  city: 'City',
  zipcode: 'ZIP code',
  state: 'State',
}

const stateLabel = (state: StatesListResponse[number]) =>
  state.name ? `${state.name} (${state.abbreviation})` : (state.abbreviation ?? '')

/**
 * The state part of the address second factor. A picker rather than a free-text box: the server
 * resolves the value against the State table and refuses one it does not recognise, so a typo here
 * would be a failed merge rather than a near miss.
 */
const StateFilterField = ({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) => {
  const { data: states, isPending: isLoading } = useStates()
  const options = useMemo(() => (states ?? []).filter(state => state.abbreviation), [states])
  // Seeded from the source's card, whose state is free text and may name nothing the State table
  // holds. Showing it rather than a blank keeps the picker honest about what the search will send,
  // so ops sees the bad value and replaces it instead of submitting it unseen. It has no real name,
  // which is what leaves `stateLabel` rendering the bare value.
  const selected = useMemo(
    () =>
      options.find(state => state.abbreviation === value) ??
      (value ? ({ abbreviation: value, name: '' } as StatesListResponse[number]) : null),
    [options, value],
  )
  return (
    <Autocomplete
      options={options}
      loading={isLoading}
      disabled={isLoading}
      size="small"
      fullWidth
      getOptionLabel={stateLabel}
      isOptionEqualToValue={(option, selected) => option.abbreviation === selected.abbreviation}
      value={selected}
      onChange={(_event, next) => onChange(next?.abbreviation ?? '')}
      renderInput={params => (
        <TextField
          {...params}
          label="State"
          required
          slotProps={{
            ...params.slotProps,
            htmlInput: { ...params.slotProps.htmlInput, 'data-testid': 'merge-filter-state' },
          }}
        />
      )}
    />
  )
}

// Fixed width reserved for the changed-details notice's leading icon. The diff rows indent by the
// same icon-column + gap so their labels line up under the header text, not under the icon.
const NOTICE_ICON_COL_PX = 20

interface MergeErrorBody {
  error_key?: string
}

// One broad, human reason per failure.
const BROAD_REASON: Record<string, string> = {
  merge_failed: "This account can't be merged — Please contact tech support.",
  source_not_unknown: 'This account is no longer an unknown account and cannot be merged.',
  destination_not_verified: 'The selected destination is not a verified account.',
  source_and_destination_equal: 'Source and destination are the same account.',
}

/** A source identity field whose searched value differs from the source profile — surfaced on the
 * confirm stage so ops attests to the change the merge will persist onto the source. */
interface ChangedField {
  label: string
  original: string | null
  entered: string
}

const personName = (first: string | null, last: string | null) =>
  [first, last].filter(Boolean).join(' ').trim()

const normalize = (value: string | null | undefined) => (value ?? '').trim().toLowerCase()

/**
 * Ops merge tool
 * Stage 1 searches the verified pool for a destination (non-filter identity
 * fields come back masked server-side); Stage 2 confirms the merge, which
 * overwrites this source Person's identity with the searched values (when ops
 * attests to the changes), soft-deletes the source, and moves its cases / tasks
 * / phone calls to the surviving verified destination, then redirects ops there.
 */
const VerifiedMergeComposer: FC<VerifiedMergeComposerProps> = ({ source }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [open, setOpen] = useState(false)
  const [stage, setStage] = useState<Stage>('search')
  const [secondFactor, setSecondFactor] = useState<SecondFactor>('member_id')
  const [filters, setFilters] = useState<Record<SearchFilterKey, string>>({
    first_name: source.firstName ?? '',
    last_name: source.lastName ?? '',
    dob: source.dob ?? '',
    member_id: source.memberId ?? '',
    street_address: source.streetAddress ?? '',
    city: source.city ?? '',
    zipcode: source.zipcode ?? '',
    state: source.state ?? '',
  })
  const [submittedParams, setSubmittedParams] = useState<VerifiedPersonSearchParams | null>(null)
  const [destination, setDestination] = useState<VerifiedPerson | null>(null)
  const [confirmed, setConfirmed] = useState(false)

  const {
    data: results,
    isFetching,
    isError: searchIsError,
  } = useVerifiedPersonSearch(submittedParams)
  const merge = useMergePersonInto(source.id)

  const sourceName = personName(source.firstName, source.lastName) || `Person ${source.id}`

  // Demographics plus whichever second factor is active — all required, so a candidate is always
  // matched on name + DOB + the chosen second factor, and this keeps ops from listing the whole
  // verified pool.
  const activeFields = useMemo(
    () => [...DEMOGRAPHIC_FIELDS, ...SECOND_FACTOR_FIELDS[secondFactor]],
    [secondFactor],
  )
  const canSearch = useMemo(
    () => activeFields.every(([key]) => filters[key].trim() !== ''),
    [activeFields, filters],
  )

  // A merge can proceed only when the search returns exactly one candidate;
  // multiple matches are ambiguous (handled as a tech-support case in the UI).
  const matches = results ?? []
  const singleMatch = submittedParams != null && !isFetching && matches.length === 1

  // The searched fields whose value differs from the source profile — these are what the merge
  // overwrites onto the source, so ops must attest to them on the confirm stage.
  const changedFields = useMemo<ChangedField[]>(() => {
    if (!submittedParams) return []
    const sourceValues: Record<SearchFilterKey, string | null> = {
      first_name: source.firstName,
      last_name: source.lastName,
      dob: source.dob,
      member_id: source.memberId,
      street_address: source.streetAddress,
      city: source.city,
      zipcode: source.zipcode,
      state: source.state,
    }
    return activeFields.flatMap(([key]) => {
      const entered = (submittedParams[key] ?? '').trim()
      const original = sourceValues[key]
      return normalize(original) === normalize(entered)
        ? []
        : [{ label: CONFIRM_FIELD_LABEL[key], original, entered }]
    })
  }, [submittedParams, source, activeFields])
  const hasChanges = changedFields.length > 0

  const resetAndClose = () => {
    setOpen(false)
    setStage('search')
    setSecondFactor('member_id')
    setSubmittedParams(null)
    setDestination(null)
    setConfirmed(false)
    merge.reset()
  }

  const handleSecondFactorChange = (next: SecondFactor) => {
    setSecondFactor(next)
    // The prior search's results/params were for the other factor — stale once the factor changes.
    setSubmittedParams(null)
  }

  const handleSearch = () => {
    if (!canSearch) return
    const demographics = {
      first_name: filters.first_name.trim(),
      last_name: filters.last_name.trim(),
      dob: filters.dob.trim(),
    }
    setSubmittedParams(
      secondFactor === 'member_id'
        ? { ...demographics, member_id: filters.member_id.trim() }
        : {
            ...demographics,
            street_address: filters.street_address.trim(),
            city: filters.city.trim(),
            zipcode: filters.zipcode.trim(),
            state: filters.state.trim(),
          },
    )
  }

  const handleContinue = () => {
    const [match] = matches
    if (!match) return
    setDestination(match)
    setStage('confirm')
    setConfirmed(false)
    merge.reset()
  }

  const handleBack = () => {
    setStage('search')
    setConfirmed(false)
    merge.reset()
  }

  const handleConfirm = () => {
    if (!destination || !submittedParams) return
    merge.mutate(
      {
        destinationPersonId: destination.id,
        firstName: submittedParams.first_name,
        lastName: submittedParams.last_name,
        dob: submittedParams.dob,
        memberId: submittedParams.member_id,
        streetAddress: submittedParams.street_address,
        city: submittedParams.city,
        zipcode: submittedParams.zipcode,
        state: submittedParams.state,
        isConfirmedByOps: hasChanges && confirmed,
      },
      {
        // Toast the audit-log id and redirect to the surviving destination.
        onSuccess: data => {
          dispatch(
            queueNotification({
              variant: 'success',
              message: `Accounts merged · audit log #${data.person_merge_log_id}`,
            }),
          )
          resetAndClose()
          void navigate(`/people/${data.destination_person_id}`)
        },
      },
    )
  }

  const mergeError = merge.isError
    ? ((merge.error as AxiosError<MergeErrorBody>).response?.data ?? {})
    : null

  const setFilter = (key: SearchFilterKey, value: string) =>
    setFilters(prev => ({ ...prev, [key]: value }))

  // When the searched details changed the source, ops must attest (check the box) before confirming.
  const confirmDisabled = merge.isPending || (hasChanges && !confirmed)

  return (
    <>
      <Button
        data-testid="merge-trigger"
        variant="outlined"
        size="small"
        startIcon={<CallMergeRoundedIcon />}
        onClick={() => setOpen(true)}
        sx={{ borderRadius: 999, whiteSpace: 'nowrap' }}
      >
        Merge
      </Button>

      <Dialog
        open={open}
        onClose={merge.isPending ? undefined : resetAndClose}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 1.5 } } }}
      >
        {/* Header */}
        <Box
          sx={theme => ({
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            px: 2.5,
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.8rem', fontWeight: 600, color: 'text.primary' }}>
              {stage === 'confirm' ? 'Confirm merge' : 'Merge into verified account'}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: '1.3rem', color: 'text.secondary' }}>
              {stage === 'confirm'
                ? "Review the source and destination, then confirm. This can't be undone here."
                : 'Search the verified pool for the destination account.'}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            size="small"
            onClick={resetAndClose}
            disabled={merge.isPending}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ px: 2.5, py: 2, maxHeight: '60vh', overflowY: 'auto' }}>
          {stage === 'search' ? (
            <SearchStage
              source={source}
              sourceName={sourceName}
              secondFactor={secondFactor}
              onSecondFactorChange={handleSecondFactorChange}
              filters={filters}
              setFilter={setFilter}
              canSearch={canSearch}
              onSearch={handleSearch}
              searched={submittedParams != null}
              isFetching={isFetching}
              isError={searchIsError}
              results={matches}
            />
          ) : (
            <ConfirmStage
              source={source}
              sourceName={sourceName}
              destination={required(destination, 'a destination on the confirm stage')}
              changedFields={changedFields}
              confirmed={confirmed}
              onConfirmedChange={setConfirmed}
              mergeError={mergeError}
            />
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.75 }}>
          {stage === 'search' ? (
            <>
              <Button sx={{ ml: 'auto' }} color="inherit" onClick={resetAndClose}>
                Cancel
              </Button>
              {/* Only a single, unambiguous match can be carried into confirm. */}
              {singleMatch && (
                <Button variant="contained" onClick={handleContinue} sx={{ borderRadius: 999 }}>
                  Continue
                </Button>
              )}
            </>
          ) : (
            <>
              <Button color="inherit" onClick={handleBack} disabled={merge.isPending}>
                ← Back
              </Button>
              <Button
                sx={{ ml: 'auto' }}
                color="error"
                variant="contained"
                onClick={handleConfirm}
                disabled={confirmDisabled}
                startIcon={
                  merge.isPending ? <CircularProgress size={16} color="inherit" /> : undefined
                }
              >
                Confirm merge
              </Button>
            </>
          )}
        </Box>
      </Dialog>
    </>
  )
}

interface SearchStageProps {
  source: MergeSource
  sourceName: string
  secondFactor: SecondFactor
  onSecondFactorChange: (next: SecondFactor) => void
  filters: Record<SearchFilterKey, string>
  setFilter: (key: SearchFilterKey, value: string) => void
  canSearch: boolean
  onSearch: () => void
  searched: boolean
  isFetching: boolean
  isError: boolean
  results: VerifiedPerson[]
}

const SearchStage: FC<SearchStageProps> = ({
  source,
  sourceName,
  secondFactor,
  onSecondFactorChange,
  filters,
  setFilter,
  canSearch,
  onSearch,
  searched,
  isFetching,
  isError,
  results,
}) => (
  <>
    {/* Source banner */}
    <Box
      sx={theme => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1.25,
        mb: 2,
        borderRadius: 1,
        bgcolor: theme.palette.tones.warning.bg,
      })}
    >
      <StatusPill tone="warning" dot label="Unknown" />
      <Typography sx={{ fontSize: '1.35rem', fontWeight: 600, color: 'text.primary' }}>
        Merging from {sourceName}
      </Typography>
      <Typography
        sx={theme => ({
          ml: 'auto',
          fontFamily: theme.typography.fontFamilyMono,
          fontSize: '1.2rem',
          color: theme.palette.tones.warning.fg,
        })}
      >
        ID {source.id}
      </Typography>
    </Box>

    <Typography
      sx={{
        fontSize: '1.1rem',
        fontWeight: 600,
        letterSpacing: '.05em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        mb: 1.25,
      }}
    >
      Find verified destination
    </Typography>

    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
      {DEMOGRAPHIC_FIELDS.map(([key, label]) => (
        <TextField
          key={key}
          label={label}
          required
          value={filters[key]}
          onChange={e => setFilter(key, e.target.value)}
          size="small"
          fullWidth
          slotProps={{ htmlInput: { 'data-testid': `merge-filter-${key}` } }}
        />
      ))}
    </Box>

    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, mb: 1.5 }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={secondFactor}
        onChange={(_e, next: SecondFactor | null) => next && onSecondFactorChange(next)}
        aria-label="Second factor"
        sx={{
          '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: '1.1rem' },
        }}
      >
        <ToggleButton value="member_id" data-testid="merge-second-factor-member_id">
          Member ID
        </ToggleButton>
        <ToggleButton value="address" data-testid="merge-second-factor-address">
          Address
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>

    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
      {SECOND_FACTOR_FIELDS[secondFactor].map(([key, label]) =>
        key === 'state' ? (
          <StateFilterField
            key={key}
            value={filters.state}
            onChange={next => setFilter('state', next)}
          />
        ) : (
          <TextField
            key={key}
            label={label}
            required
            value={filters[key]}
            onChange={e => setFilter(key, e.target.value)}
            size="small"
            fullWidth
            slotProps={{ htmlInput: { 'data-testid': `merge-filter-${key}` } }}
          />
        ),
      )}
    </Box>

    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
      <Button
        variant="contained"
        onClick={onSearch}
        disabled={!canSearch}
        sx={{ borderRadius: 999 }}
      >
        Search verified pool
      </Button>
    </Box>

    {searched && (
      <Box sx={theme => ({ mt: 2.5, pt: 2, borderTop: `1px solid ${theme.palette.divider}` })}>
        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError ? (
          <Typography
            data-testid="merge-search-error"
            sx={theme => ({ fontSize: '1.3rem', color: theme.palette.tones.error.fg })}
          >
            Search failed. Check the details and try again.
          </Typography>
        ) : results.length === 0 ? (
          <Typography sx={{ fontSize: '1.3rem', color: 'text.secondary' }}>
            No verified matches for these filters.
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: '1.3rem', fontWeight: 600, mb: 1.5 }}>
              {results.length} verified {results.length === 1 ? 'match' : 'matches'}
            </Typography>
            {/* A merge needs exactly one destination; more than one is a tech-support escalation. */}
            {results.length > 1 && <MultiMatchNotice count={results.length} />}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {results.map(row => (
                <ResultRow key={row.id} row={row} />
              ))}
            </Box>
          </>
        )}
      </Box>
    )}
  </>
)

const MultiMatchNotice: FC<{ count: number }> = ({ count }) => (
  <Box
    data-testid="merge-multi-match"
    sx={theme => ({
      display: 'flex',
      gap: 1.25,
      p: 1.5,
      mb: 2,
      borderRadius: 1,
      bgcolor: theme.palette.tones.warning.bg,
      color: theme.palette.tones.warning.fg,
    })}
  >
    <WarningAmberRoundedIcon fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
    <Box>
      <Typography sx={{ fontSize: '1.3rem', fontWeight: 700 }}>
        Can&apos;t merge — more than one verified match
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: '1.25rem', lineHeight: 1.5 }}>
        A merge needs exactly one destination. {count} verified accounts share these details, so
        escalate to the tech team in the support Slack channel to investigate.
      </Typography>
    </Box>
  </Box>
)

const ResultRow: FC<{ row: VerifiedPerson }> = ({ row }) => {
  const rowName = personName(row.first_name, row.last_name) || '—'
  const fields: Array<[string, string | null]> = [
    ['Date of birth', row.dob],
    ['Phone', row.phone_number],
    ['Email', row.email],
    ['Member ID', row.member_id],
    ['Street address', row.street_address],
    ['City', row.city],
    ['ZIP code', row.zipcode],
    ['State', row.state],
  ]
  return (
    <Box
      data-testid={`merge-result-${row.id}`}
      sx={theme => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        p: 1.75,
        bgcolor: 'background.paper',
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <StatusPill tone="success" dot label="Verified" />
        <Typography sx={{ fontSize: '1.45rem', fontWeight: 600, color: 'text.primary' }}>
          {rowName}
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.25, mt: 1.5 }}>
        {fields.map(([label, value]) => (
          <Box key={label} sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                letterSpacing: '.04em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{ fontSize: '1.25rem', color: 'text.primary', overflowWrap: 'anywhere' }}
            >
              {value ?? '—'}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

interface ConfirmStageProps {
  source: MergeSource
  sourceName: string
  destination: VerifiedPerson
  changedFields: ChangedField[]
  confirmed: boolean
  onConfirmedChange: (checked: boolean) => void
  mergeError: MergeErrorBody | null
}

const ConfirmStage: FC<ConfirmStageProps> = ({
  source,
  sourceName,
  destination,
  changedFields,
  confirmed,
  onConfirmedChange,
  mergeError,
}) => {
  const destName =
    personName(destination.first_name, destination.last_name) || `Person ${destination.id}`
  return (
    <>
      {/* Destructive-action warning */}
      <Box
        sx={theme => ({
          display: 'flex',
          gap: 1.25,
          p: 1.5,
          borderRadius: 1,
          bgcolor: theme.palette.tones.error.bg,
          color: theme.palette.tones.error.fg,
        })}
      >
        <ErrorRoundedIcon fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '1.3rem', lineHeight: 1.5 }}>
          <Box component="strong" sx={{ fontWeight: 700 }}>
            {sourceName}
          </Box>{' '}
          (unknown) will be merged into the verified destination below. The source account is{' '}
          <Box component="strong" sx={{ fontWeight: 700 }}>
            soft-deleted
          </Box>
          , and its cases, tasks, and phone calls move to the destination.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 2 }}>
        <PersonCard
          tone="warning"
          toneLabel="Unknown"
          slotLabel="Source"
          name={sourceName}
          id={source.id}
        />
        <PersonCard
          tone="success"
          toneLabel="Verified"
          slotLabel="Destination"
          name={destName}
          id={destination.id}
        />
      </Box>

      {changedFields.length > 0 && (
        <ChangedDetailsNotice
          changedFields={changedFields}
          confirmed={confirmed}
          onConfirmedChange={onConfirmedChange}
        />
      )}

      {mergeError && <MergeErrorNotice error={mergeError} />}
    </>
  )
}

/** Blue attestation panel: the searched values differ from the source profile, so the merge will
 * overwrite the source with them — ops confirms they verified the changes with the member. */
const ChangedDetailsNotice: FC<{
  changedFields: ChangedField[]
  confirmed: boolean
  onConfirmedChange: (checked: boolean) => void
}> = ({ changedFields, confirmed, onConfirmedChange }) => (
  <Box
    data-testid="merge-changed-details"
    sx={theme => ({
      mt: 2,
      borderRadius: 1,
      overflow: 'hidden',
      bgcolor: theme.palette.tones.info.bg,
    })}
  >
    <Box sx={{ display: 'flex', gap: 1.25, p: 1.5 }}>
      <Box sx={{ width: `${NOTICE_ICON_COL_PX}px`, flexShrink: 0, display: 'flex', mt: 0.25 }}>
        <InfoRoundedIcon fontSize="small" sx={theme => ({ color: theme.palette.tones.info.fg })} />
      </Box>
      <Box>
        <Typography
          sx={theme => ({
            fontSize: '1.3rem',
            fontWeight: 700,
            color: 'text.primary',
          })}
        >
          You changed details to find this match
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: '1.25rem', lineHeight: 1.5, color: 'text.secondary' }}>
          These values differ from the source profile. Confirm you verified them with the member —
          on merge, the source&apos;s original values are overwritten with what you entered.
        </Typography>
      </Box>
    </Box>

    {changedFields.map(field => (
      <Box
        key={field.label}
        sx={theme => ({ borderTop: `1px solid ${theme.palette.tones.info.fg}1f` })}
      >
        <Box
          sx={theme => ({
            display: 'grid',
            gridTemplateColumns: '108px 1fr',
            columnGap: 2,
            alignItems: 'baseline',
            // Align the label under the header text block: icon column + its gap past the padding.
            pl: `calc(${theme.spacing(1.5)} + ${NOTICE_ICON_COL_PX}px + ${theme.spacing(1.25)})`,
            pr: 1.5,
            py: 1.25,
          })}
        >
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              letterSpacing: '.04em',
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            {field.label}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 1,
              flexWrap: 'wrap',
              fontSize: '1.25rem',
              overflowWrap: 'anywhere',
            }}
          >
            {field.original ? (
              <Box
                component="span"
                sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
              >
                {field.original}
              </Box>
            ) : (
              <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                Not captured
              </Box>
            )}
            <Box component="span" sx={{ color: 'text.secondary' }}>
              →
            </Box>
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {field.entered}
            </Box>
          </Box>
        </Box>
      </Box>
    ))}

    <Box sx={theme => ({ borderTop: `1px solid ${theme.palette.tones.info.fg}1f` })}>
      <FormControlLabel
        sx={{ px: 1.5, py: 1, m: 0, alignItems: 'center' }}
        control={
          <Checkbox
            size="small"
            disableRipple
            checked={confirmed}
            onChange={e => onConfirmedChange(e.target.checked)}
            sx={theme => ({ p: 0, mr: 1, color: 'text.secondary' })}
          />
        }
        label={
          <Typography
            sx={theme => ({
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'text.primary',
            })}
          >
            I verified these details with the member and confirm they&apos;re correct.
          </Typography>
        }
      />
    </Box>
  </Box>
)

const PersonCard: FC<{
  tone: 'warning' | 'success'
  toneLabel: string
  slotLabel: string
  name: string
  id: number
}> = ({ tone, toneLabel, slotLabel, name, id }) => (
  <Box
    sx={theme => ({
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 1,
      p: 1.5,
    })}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
      <StatusPill tone={tone} dot label={toneLabel} />
      <Typography
        sx={{
          fontSize: '1rem',
          fontWeight: 600,
          letterSpacing: '.05em',
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        {slotLabel}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, color: 'text.primary' }}>
      {name}
    </Typography>
    <Typography
      sx={theme => ({
        mt: 0.5,
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: '1.2rem',
        color: 'text.secondary',
      })}
    >
      ID {id}
    </Typography>
  </Box>
)

const MergeErrorNotice: FC<{ error: MergeErrorBody }> = ({ error }) => {
  const message =
    (error.error_key && BROAD_REASON[error.error_key]) || 'The merge could not be completed.'

  return (
    <Box
      data-testid="merge-error"
      sx={theme => ({
        mt: 2,
        p: 1.5,
        borderRadius: 1,
        border: `1px solid ${theme.palette.tones.error.fg}`,
        bgcolor: theme.palette.tones.error.bg,
        color: theme.palette.tones.error.fg,
      })}
    >
      <Typography sx={{ fontSize: '1.3rem', lineHeight: 1.5 }}>{message}</Typography>
    </Box>
  )
}

export default VerifiedMergeComposer
